/**
 * YG Article Product Loader
 * 获取完整产品页面 HTML，提取 yg-article-product section
 */

class YGArticleProductLoader {
  constructor(options = {}) {
    this.placeholderSelector = "yg-article-product[data-handle]";
    this.targetSelector =
      options.targetSelector ||
      '.yg-article-product, [class*="yg-article-product"]';
    this.loadedHandles = new Set();
    this.cache = new Map();

    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.loadProducts());
    } else {
      this.loadProducts();
    }
  }

  getProductHandles() {
    const placeholders = document.querySelectorAll(this.placeholderSelector);
    const handles = [];

    placeholders.forEach((el) => {
      const handle = el.getAttribute("data-handle");
      if (handle && !this.loadedHandles.has(handle)) {
        handles.push({
          handle: handle,
          element: el,
          url: this.buildUrl(handle),
        });
      }
    });

    return handles;
  }

  /**
   * 构建产品页面 URL（不带参数，获取完整 HTML）
   */
  buildUrl(productHandle) {
    return `${window.location.origin}/products/${productHandle}`;
  }

  async loadProducts() {
    const products = this.getProductHandles();
    if (products.length === 0) return;

    const results = await Promise.allSettled(
      products.map((p) => this.fetchProduct(p)),
    );

    const loaded = results.filter((r) => r.status === "fulfilled").length;
    console.log(
      `[YG Article Product] Loaded ${loaded}/${products.length} products`,
    );
  }

  /**
   * 获取完整产品页面 HTML，解析提取目标 section
   */
  async fetchProduct(product) {
    // 检查缓存
    if (this.cache.has(product.handle)) {
      this.render(
        product.element,
        this.cache.get(product.handle),
        product.handle,
      );
      return;
    }

    try {
      console.log("[YG Article Product] Fetching page:", product.url);

      const response = await fetch(product.url, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Product "${product.handle}" not found`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();

      // 使用 DOMParser 解析 HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // 查找目标元素：优先找 yg-article-product，其次找 section 容器
      let targetElement = doc.querySelector("yg-article-product");

      if (!targetElement) {
        // 尝试从 section 容器中查找
        const sectionContainer = doc.querySelector(
          ".section.yg-article-product, " +
            '[class*="yg-article-product"], ' +
            '[id*="yg-article-product"], ' +
            '#shopify-section-template--[class*="yg"]',
        );

        if (sectionContainer) {
          targetElement =
            sectionContainer.querySelector("yg-article-product") ||
            sectionContainer;
        }
      }

      if (!targetElement) {
        console.warn(
          "[YG Article Product] Target element not found in page, available sections:",
          Array.from(doc.querySelectorAll('[id^="shopify-section"]')).map(
            (el) => el.id,
          ),
        );
        throw new Error("yg-article-product section not found in product page");
      }

      console.log(
        "[YG Article Product] Found element:",
        targetElement.tagName,
        targetElement.className,
      );

      // 获取 HTML 字符串
      const targetHtml = targetElement.outerHTML;

      // 缓存并渲染
      this.cache.set(product.handle, targetHtml);
      this.render(product.element, targetHtml, product.handle);
    } catch (error) {
      console.error(
        `[YG Article Product] Failed to load ${product.handle}:`,
        error,
      );
      this.showError(product.element, product.handle, error.message);
    }
  }

  /**
   * 渲染到页面
   */
  render(element, html, handle) {
    // 创建临时容器解析
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html.trim();

    let newContent = tempDiv.firstElementChild;

    if (!newContent) {
      throw new Error("Failed to parse HTML");
    }

    // 更新属性
    newContent.setAttribute("data-handle", handle);
    newContent.setAttribute("data-loaded", "true");

    // 替换占位符
    element.replaceWith(newContent);
    this.loadedHandles.add(handle);

    // 重新初始化内部功能（Swiper、Shopify 特性等）
    this.reinitializeContent(newContent);

    // 触发自定义事件
    document.dispatchEvent(
      new CustomEvent("yg:productLoaded", {
        detail: { handle, element: newContent },
      }),
    );
  }

  /**
   * 重新初始化内容（参考你提供的代码模式）
   */
  reinitializeContent(container) {
    // 1. 防止 ID 重复（页面可能有多个相同产品）
    this.preventDuplicatedIDs(container);

    // 2. 初始化 Shopify 特性（加入购物车按钮）
    this.initShopifyFeatures(container);

    // 3. 更新图片尺寸
    this.updateImageSizes(container);

    this.initVariants(container);

    // 4. 初始化下载弹窗（单产品模式，无需循环）
    this.initDownloadItems(container);
  }

  /**
   * 初始化下载弹窗（单产品专用）
   */
  initDownloadItems(container) {
    const downloadCards = container.querySelectorAll('.yg-product-card-download');
    const downloadItems = container.querySelectorAll('.yg-product-download-item');

    downloadCards.forEach(downloadCard => {
      const downloadList = downloadCard.querySelector('.yg-product-download-list');
      if (!downloadCard || !downloadList) return;

      const button = downloadCard.querySelector('button');
      button?.addEventListener('click', (e) => {
        e.stopPropagation();
        downloadCard.classList.toggle('active');
      });
    });

    downloadItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const downloadList = item.closest('.yg-product-download-list');
        const downloadCard = item.closest('.yg-product-card-download');
        if (!downloadList || !downloadCard) return;

        const items = Array.from(downloadList.querySelectorAll('.yg-product-download-item'));
        const clickedIndex = items.indexOf(item);

        // 让所有卡片的对应 index 项变为 active
        const allLists = container.querySelectorAll('.yg-product-download-list');
        allLists.forEach(list => {
          const listItems = list.querySelectorAll('.yg-product-download-item');
          listItems.forEach((listItem, idx) => {
            listItem.classList.toggle('active', idx === clickedIndex);
          });
        });

        // 卡片切换到对应的 index
        const cardItem = downloadCard.closest('.yg-article-product-card');
        if (!cardItem) return;

        const cards = container.querySelectorAll('.yg-article-product-card');
        cards.forEach((card, index) => {
          if (index === clickedIndex) {
            card.classList.add('active');
          } else {
            card.classList.remove('active');
          }
        });

        downloadCard.classList.remove('active');
      });
    });

    // 点击外部关闭
    const handleOutsideClick = (e) => {
      downloadCards.forEach(card => {
        if (!card.contains(e.target)) {
          card.classList.remove('active');
        }
      });
    };
    document.addEventListener('click', handleOutsideClick);

    // 清理旧的事件监听器
    const cleanupKey = `downloadCleanup_${Date.now()}`;
    container[cleanupKey] = () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }

  /**
   * 初始化 Shopify 特性（加入购物车按钮）
   */
  initShopifyFeatures(container) {
    const addToCartButtons = container.querySelectorAll('.yg-article-product-card__btn');
    console.log("addToCartButtons: ", addToCartButtons)
    addToCartButtons.forEach(button => {
      // 只有 button 元素才拦截点击行为，链接保持默认跳转
      if (button.tagName !== 'BUTTON') return;

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart(button);
      });
    });
  }

  /**
   * 防止 ID 重复（多个相同产品卡片时）
   */
  preventDuplicatedIDs(container) {
    const uniqueSuffix = `-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    container.querySelectorAll("[id]").forEach((el) => {
      const oldId = el.id;
      el.id = `${oldId}${uniqueSuffix}`;
      // 更新关联的 label/for 属性
      container.querySelectorAll(`[for="${oldId}"]`).forEach((label) => {
        label.setAttribute("for", el.id);
      });
    });
  }

  initVariants(container) {
    const variants = container.querySelectorAll('.yg-article-product-card__color-swatches .card__product-color-list__button');
    variants.forEach(variant => {
      // 点击事件
      variant.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const addToCartButton = variant.closest('.yg-article-product-card').querySelector('.yg-article-product-card__btn');

        // 获取当前 variant 的 data-id
        const variantId = variant.getAttribute('data-id');

        // 更新按钮的 data-variant-id
        if (addToCartButton && variantId) {
          addToCartButton.setAttribute('data-variant-id', variantId);
        }
      });

      // 鼠标移入事件（可选，如果只需要点击，可以删除这部分）
      variant.addEventListener('mouseenter', () => {
        const variantId = variant.getAttribute('data-id');
        const addToCartButton = variant.closest('.yg-article-product-card').querySelector('.yg-article-product-card__btn');

        if (addToCartButton && variantId) {
          addToCartButton.setAttribute('data-variant-id', variantId);
        }
      });
    });
  }

  /**
   * 处理加入购物车
   */
  async handleAddToCart(button) {
    console.log("handleAddToCart button: ", button)
    const variantId = button.getAttribute('data-variant-id');

    if (!variantId) {
      console.error('[YG Article Product] No variant ID found');
      return;
    }

    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    const textSpan = button.querySelector('span');
    const originalText = textSpan?.textContent || button.textContent;

    try {
      if (textSpan) {
        textSpan.textContent = '...';
      } else {
        button.textContent = '...';
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData,
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.description || result.errors[0] || 'Failed to add to cart');
      }

      document.dispatchEvent(new CustomEvent('afterCartChanged', { detail: { productVariantId: variantId } }));

      if (cartDrawer && result.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(result);
      }

      button.classList.add('active');
      if (textSpan) {
        textSpan.textContent = 'Added';
      } else {
        button.textContent = 'Added';
      }

      setTimeout(() => {
        button.classList.remove('active');
        if (textSpan) {
          textSpan.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 3000);

    } catch (error) {
      console.error('[YG Article Product] Add to cart error:', error.message);
      if (textSpan) {
        textSpan.textContent = 'Error';
      } else {
        button.textContent = 'Error';
      }
      setTimeout(() => {
        if (textSpan) {
          textSpan.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 2000);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /**
   * 更新图片尺寸（响应式）
   */
  updateImageSizes(container) {
    const images = container.querySelectorAll(
      "img[data-src], img[data-srcset]",
    );
    images.forEach((img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      img.classList.add("lazyloaded");
    });
  }

  /**
   * 显示通知
   */
  showNotification(message, type = "info") {
    // 可以集成你的主题通知系统
    if (window.theme && window.theme.showNotification) {
      window.theme.showNotification(message, type);
    } else {
      console.log(`[${type}] ${message}`);
    }
  }

  /**
   * 显示错误状态
   */
  showError(element, handle, message) {
    element.setAttribute("data-error", "true");
    element.innerHTML = `
        <div class="yg-product-error" style="padding: 20px; border: 1px solid #ddd; border-radius: 8px; background: #fff;">
          <p style="margin: 0 0 8px; color: #333; font-weight: 500;">Unable to load product</p>
          <p style="margin: 0 0 12px; font-size: 12px; color: #666; word-break: break-all;">${handle}</p>
          <p style="margin: 0 0 12px; font-size: 11px; color: #999;">${message}</p>
          <button onclick="window.ygArticleProductLoader.retry('${handle}')" 
                  style="padding: 8px 16px; cursor: pointer; background: #000; color: #fff; border: none; border-radius: 4px; font-size: 13px;">
            Retry
          </button>
        </div>
      `;
  }

  /**
   * 重试加载
   */
  retry(handle) {
    this.loadedHandles.delete(handle);
    this.cache.delete(handle);

    const element = document.querySelector(
      `yg-article-product[data-handle="${handle}"]`,
    );
    if (element) {
      element.innerHTML = "";
      element.removeAttribute("data-error");
      element.removeAttribute("data-error-msg");

      const product = {
        handle: handle,
        element: element,
        url: this.buildUrl(handle),
      };
      this.fetchProduct(product);
    }
  }
}

// 初始化
window.ygArticleProductLoader = new YGArticleProductLoader({
  targetSelector: ".yg-article-product, yg-article-product",
});

/**
 * YG Article Product List Loader
 * 获取产品列表页面 HTML，提取 yg-article-product-list section
 */

class YGArticleProductListLoader {
  constructor(options = {}) {
    this.placeholderSelector = 'yg-article-product-list[data-handle]';
    this.targetSelector = options.targetSelector || '.yg-article-product-list, yg-article-product-list';
    this.loadedHandles = new Set();
    this.cache = new Map();
    this.baseUrl = window.location.origin;
    
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.loadProductLists());
    } else {
      this.loadProductLists();
    }
  }

  getProductListHandles() {
    const placeholders = document.querySelectorAll(this.placeholderSelector);
    const handles = [];
    
    placeholders.forEach(el => {
      const handleAttr = el.getAttribute('data-handle');
      if (!handleAttr) return;
      
      // 解析逗号分隔的多个产品 handle
      const productHandles = handleAttr
        .split(',')
        .map(h => h.trim())
        .filter(h => h.length > 0);
      
      const listKey = handleAttr;
      
      if (!this.loadedHandles.has(listKey)) {
        handles.push({
          key: listKey,
          handles: productHandles,
          element: el
        });
      }
    });
    
    return handles;
  }

  /**
   * 构建单个产品的 URL（类似 YGArticleProductLoader）
   */
  buildProductUrl(handle) {
    // 使用产品页面 + 自定义视图参数
    return `${this.baseUrl}/products/${handle}`;
  }

  async loadProductLists() {
    const productLists = this.getProductListHandles();
    if (productLists.length === 0) return;

    // 逐个列表处理
    for (const list of productLists) {
      await this.fetchProductList(list);
    }
  }

  async fetchProductList(productList) {
    // 检查缓存 - 如果所有产品都已缓存，直接渲染
    const cachedProducts = [];
    let allCached = true;
    
    for (const handle of productList.handles) {
      if (this.cache.has(handle)) {
        cachedProducts.push(this.cache.get(handle));
      } else {
        allCached = false;
        break;
      }
    }
    
    if (allCached) {
      this.render(productList.element, cachedProducts, productList.key);
      return;
    }

    try {
      console.log(`[YG Article Product List] Fetching ${productList.handles.length} products for:`, productList.key);
      
      // 每个 handle 单独请求（类似单产品逻辑）
      const fetchPromises = productList.handles.map(handle => this.fetchSingleProduct(handle));
      const results = await Promise.allSettled(fetchPromises);
      
      const products = [];
      const errors = [];
      
      results.forEach((result, index) => {
        const handle = productList.handles[index];
        if (result.status === 'fulfilled') {
          products.push(result.value);
        } else {
          errors.push({ handle, error: result.reason });
          console.warn(`[YG Article Product List] Failed to load ${handle}:`, result.reason);
        }
      });
      
      if (products.length === 0) {
        throw new Error(`All products failed to load: ${errors.map(e => e.handle).join(', ')}`);
      }

      console.log(`[YG Article Product List] Successfully loaded ${products.length}/${productList.handles.length} products`);
      
      // 渲染
      this.render(productList.element, products, productList.key);
      
    } catch (error) {
      console.error(`[YG Article Product List] Failed to load list ${productList.key}:`, error);
      this.showError(productList.element, productList.key, error.message);
    }
  }

  /**
   * 获取单个产品页面中的产品列表（.yg-article-product-list-products）
   */
  async fetchSingleProduct(handle) {
    // 检查缓存
    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    const url = this.buildProductUrl(handle);
    console.log('[YG Article Product List] Fetching product list:', url);

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // 查找产品列表容器 - .yg-article-product-list-products
    let productListElement = doc.querySelector('.yg-article-product-list-products');
    
    if (!productListElement) {
      // 备选：查找 section 容器内的产品列表
      const sectionContainer = doc.querySelector(
        '.section.yg-article-product, ' +
        '[class*="yg-article-product-list"], ' +
        '#shopify-section-template--[class*="product"]'
      );
      
      if (sectionContainer) {
        productListElement = sectionContainer.querySelector('.yg-article-product-list-products') || sectionContainer;
      }
    }

    if (!productListElement) {
      throw new Error(`Product list element not found for ${handle}`);
    }

    // 获取内部 HTML 内容并缓存（不包含外层容器）
    const productListHtml = productListElement.innerHTML;
    this.cache.set(handle, productListHtml);
    
    return productListHtml;
  }

  /**
   * 渲染：将所有产品内容追加到 .yg-article-product-list-products 容器的后面
   */
  render(element, productHtmls, key) {
    // 找到或创建产品列表容器
    let productsContainer = element.querySelector('.yg-article-product-list-products');
    
    // 如果不存在则创建并添加到元素内
    if (!productsContainer) {
      productsContainer = document.createElement('div');
      productsContainer.className = 'yg-article-product-list-products';
      element.appendChild(productsContainer);
    }
    
    // 将每个产品的内容作为新的容器追加到 .yg-article-product-list-products 的后面
    productHtmls.forEach((html, index) => {
      if (html && html.trim()) {
        const newContainer = document.createElement('div');
        newContainer.className = 'yg-article-product-list-products';
        newContainer.innerHTML = html.trim();
        
        // 防止 ID 重复
        this.preventDuplicatedIDs(newContainer, `${index}`);
        
        // 插入到现有容器的后面（兄弟元素）
        productsContainer.parentNode.insertBefore(newContainer, productsContainer.nextSibling);
      }
    });
    
    // 更新状态
    element.setAttribute('data-loaded', 'true');
    this.loadedHandles.add(key);
    
    // 重新初始化内部功能
    this.reinitializeContent(element);
    
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('yg:productListLoaded', {
      detail: { key, element, count: productHtmls.length }
    }));
  }

  /**
   * 防止 ID 重复
   */
  preventDuplicatedIDs(container, index) {
    const uniqueSuffix = `-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
    
    container.querySelectorAll('[id]').forEach(el => {
      const oldId = el.id;
      el.id = `${oldId}${uniqueSuffix}`;
      
      container.querySelectorAll(`[for="${oldId}"]`).forEach(label => {
        label.setAttribute('for', el.id);
      });
    });
  }

  /**
   * 重新初始化内容
   */
  reinitializeContent(container) {
    this.initInnerProducts(container);
    this.updateImageSizes(container);
    this.initDownloadItems(container);
    this.initShopifyFeatures(container);
    this.initVariants(container);
  }

  /**
   * 初始化内部产品卡片
   */
  initInnerProducts(container) {
    const productCards = container.querySelectorAll('yg-article-product:not([data-loaded])');
    productCards.forEach(card => {
      if (window.ygArticleProductLoader) {
        const handle = card.getAttribute('data-handle');
        if (handle) {
          window.ygArticleProductLoader.loadSingleProduct(card, handle);
        }
      }
    });
  }

  initVariants(container) {
    const variants = container.querySelectorAll('.yg-article-list-card .card__product-color-list__button');
    variants.forEach(variant => {
      // 点击事件
      variant.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const addToCartButton = variant.closest('.yg-article-list-card').querySelector('.yg-product-card-button');

        // 获取当前 variant 的 data-id
        const variantId = variant.getAttribute('data-id');

        // 更新按钮的 data-variant-id
        if (addToCartButton && variantId) {
          addToCartButton.setAttribute('data-variant-id', variantId);
        }
      });

      // 鼠标移入事件（可选，如果只需要点击，可以删除这部分）
      variant.addEventListener('mouseenter', () => {
        const variantId = variant.getAttribute('data-id');
        const addToCartButton = variant.closest('.yg-article-list-card').querySelector('.yg-product-card-button');

        if (addToCartButton && variantId) {
          addToCartButton.setAttribute('data-variant-id', variantId);
        }
      });
    });
  }

  /**
   * 初始化 Shopify 特性（加入购物车按钮）
   */
  initShopifyFeatures(container) {
    const addToCartButtons = container.querySelectorAll('.yg-product-card-button');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart(button);
      });
    });
  }

  /**
   * 初始化 dropdown
   */
  initDownloadItems(container) {
    console.log("initDownloadItems container: ", container)
    const downloadButtons = container.querySelectorAll('.yg-product-card-download button');
    const downloadItems = container.querySelectorAll('.yg-product-download-item');
    const downloadCards = container.querySelectorAll('.yg-product-card-download');

    downloadButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const downloadCard = button.closest('.yg-product-card-download');
        const downloadList = downloadCard.querySelector('.yg-product-download-list');
        if (!downloadList || !downloadCard) return;
        downloadCard.classList.toggle('active');
      });
    });
    downloadItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const downloadList = item.closest('.yg-product-download-list');
        const downloadCard = item.closest('.yg-product-card-download');
        if (!downloadList || !downloadCard) return;

        const items = Array.from(downloadList.querySelectorAll('.yg-product-download-item'));
        const clickedIndex = items.indexOf(item);

        const cardItem = downloadCard.closest('.yg-article-product-list-products');
        if (!cardItem) return;

        const cards = cardItem.querySelectorAll('.yg-product-card');
        cards.forEach((card, index) => {
          if (index === clickedIndex) {
            card.classList.add('active');
          } else {
            card.classList.remove('active');
          }
        });

        downloadCard.classList.remove('active');
      });
    });

    // 点击外部关闭所有下载弹窗
    const handleOutsideClick = (e) => {
      downloadCards.forEach(card => {
        if (!card.contains(e.target)) {
          card.classList.remove('active');
        }
      });
    };
    document.addEventListener('click', handleOutsideClick);

    // 清理旧的事件监听器
    const cleanupKey = `downloadListCleanup_${Date.now()}`;
    container[cleanupKey] = () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }

  /**
   * 处理加入购物车
   */
  async handleAddToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) {
      console.error('[YG Article Product] No variant ID found');
      return;
    }

    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    const textSpan = button.querySelector('span');
    const originalText = textSpan?.textContent || button.textContent;
    const loadingText = '...';

    try {
      if (textSpan) {
        textSpan.textContent = loadingText;
      } else {
        button.textContent = loadingText;
      }

      const cartDrawer = document.querySelector('cart-drawer');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      };

      const response = await fetch('/cart/add.js', config);
      const result = await response.json();

      if (result.errors) {
        throw new Error(result.description || result.errors[0] || 'Failed to add to cart');
      }

      button.classList.add('active');
      if (textSpan) {
        textSpan.textContent = 'Added';
      } else {
        button.textContent = 'Added';
      }

      if (cartDrawer && result.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(result);
      }

      document.dispatchEvent(new CustomEvent('afterCartChanged', { detail: { productVariantId: variantId } }));

      setTimeout(() => {
        button.classList.remove('active');
        if (textSpan) {
          textSpan.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 3000);

    } catch (error) {
      console.error('[YG Article Product] Add to cart error:', error.message);
      if (textSpan) {
        textSpan.textContent = 'Error';
      } else {
        button.textContent = 'Error';
      }
      setTimeout(() => {
        if (textSpan) {
          textSpan.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 2000);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /**
   * 更新图片尺寸
   */
  updateImageSizes(container) {
    const images = container.querySelectorAll('img[data-src], img[data-srcset]');
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      img.classList.add('lazyloaded');
    });
  }

  /**
   * 显示通知
   */
  showNotification(message, type = 'info') {
    if (window.theme && window.theme.showNotification) {
      window.theme.showNotification(message, type);
    } else {
      console.log(`[${type}] ${message}`);
    }
  }

  /**
   * 显示错误状态
   */
  showError(element, key, message) {
    element.setAttribute('data-error', 'true');
    element.innerHTML = `
      <div class="yg-product-list-error" style="padding: 20px; border: 1px solid #ddd; border-radius: 8px; background: #fff;">
        <p style="margin: 0 0 8px; color: #333; font-weight: 500;">Unable to load product list</p>
        <p style="margin: 0 0 12px; font-size: 12px; color: #666; word-break: break-all;">${key}</p>
        <p style="margin: 0 0 12px; font-size: 11px; color: #999;">${message}</p>
        <button onclick="window.ygArticleProductListLoader.retry('${key}')" 
                style="padding: 8px 16px; cursor: pointer; background: #000; color: #fff; border: none; border-radius: 4px; font-size: 13px;">
          Retry
        </button>
      </div>
    `;
  }

  /**
   * 重试加载
   */
  retry(key) {
    this.loadedHandles.delete(key);
    
    // 清除该列表下所有产品的缓存
    const element = document.querySelector(`yg-article-product-list[data-handle="${key}"]`);
    if (element) {
      const handleAttr = element.getAttribute('data-handle');
      if (handleAttr) {
        const handles = handleAttr.split(',').map(h => h.trim());
        handles.forEach(h => this.cache.delete(h));
      }
    }
    
    if (element) {
      element.innerHTML = '';
      element.removeAttribute('data-error');
      element.removeAttribute('data-loaded');
      
      const handleAttr = element.getAttribute('data-handle');
      if (handleAttr) {
        const productHandles = handleAttr
          .split(',')
          .map(h => h.trim())
          .filter(h => h.length > 0);
        
        const list = {
          key: key,
          handles: productHandles,
          element: element
        };
        this.fetchProductList(list);
      }
    }
  }
}

// 初始化
window.ygArticleProductListLoader = new YGArticleProductListLoader({
  targetSelector: '.yg-article-product-list, yg-article-product-list'
});
